///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { GtthDesignChartData } from '../../../../generated/shared-types.model';
import * as d3 from 'd3';
import { DecimalPipe } from '@angular/common';
import { ProjectProductsService } from '../../../logical-topology/engine/project-products-service';
import { GtthCustomerLocationService } from '../../../../services/customer/customer-location.service';

@Component({
    selector: 'winde-design-data-chart',
    styleUrls: ['./design-chart-data.scss'],
    template: `
        <div class="chart">
            <div>
                <label class="chart__title m-0">Limitations & Cost</label>
            </div>
            <div class="chart__block">
                <svg
                    id="chartContainer"
                    [attr.width]="width + margin.left + margin.right"
                    [attr.height]="height + margin.top + margin.bottom"
                ></svg>
            </div>
            <div class="chart-legend">
                <span class="chart-legend__item chart-legend__item--limitations" (click)="toggleSerie('limitation')">Limitations</span>
                <span class="chart-legend__item chart-legend__item--cost" (click)="toggleSerie('cost')">Cost</span>
            </div>
        </div>
    `,
    standalone: false
})
export class GtthDesignDataChartComponent implements AfterViewInit {
    public margin = { top: 30, right: 90, bottom: 30, left: 90 };
    public width = 750 - this.margin.left - this.margin.right;
    public height = 270 - this.margin.top - this.margin.bottom;
    public hoveredIteration: number;
    @Input()
    public selectedIteration: number;
    private readonly uiUpdateDuration = 500;
    private readonly fontSize = 12;
    private readonly chartLineWidth = 1.5;
    private readonly colorAxisLines = '#BCBCBC';
    private readonly colorLimitation = '#4a90e2';
    private readonly colorCost = '#32b04a';
    private readonly colorHoverLimitation = '#294673';
    private readonly colorHoverCost = '#1a572a';
    private readonly colorSelect = '#000000';
    private currencySymbol: string;
    private data: GtthDesignChartData[];
    private readonly seriesVisibility = {
        limitation: true,
        cost: true
    };
    private readonly limitTicksCount = 4;
    private readonly costTicksCount = 4;
    private xScale: any;
    private limitScale: any;
    private costScale: any;
    private xAxis: any;
    private limitAxisLeft: any;
    private costAxisRight: any;
    private limitAxisG: any;
    private costAxisG: any;
    private limitLine: any;
    private costLine: any;
    private limitPoints: any[];
    private costPoints: any[];
    @Output()
    private readonly iterationClick: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    private readonly iterationHover: EventEmitter<number> = new EventEmitter<number>();

    constructor(
        private readonly projectProductsService: ProjectProductsService,
        private readonly customerLocationService: GtthCustomerLocationService
    ) {}

    @Input()
    set chartData(chartData: GtthDesignChartData[]) {
        this.data = chartData;
        this.updateChart();
    }

    ngAfterViewInit(): void {
        this.currencySymbol = this.customerLocationService.getCurrencySymbolByCode(this.projectProductsService.getCurrencyCode());

        this.limitPoints = [];
        this.costPoints = [];

        const g = d3
            .select('#chartContainer')
            .append('g')
            .attr('class', 'rootG')
            .attr('transform', this.transformTranslateParam(this.margin.left, this.margin.top));

        this.createScales();
        this.createAxes(g);

        if (this.data && this.data.length > 0) {
            this.createCostLine(g);
            this.createLimitLine(g);
        }
    }

    public toggleSerie(serieName: string) {
        this.seriesVisibility[serieName] = !this.seriesVisibility[serieName];
        const newOpacity = this.seriesVisibility[serieName] ? 1 : 0;

        if (serieName === 'limitation') {
            d3.select('.limitAxis').style('opacity', newOpacity);
            d3.select('.limitLine').style('opacity', newOpacity);
            this.limitPoints.forEach((point) => point.style('opacity', newOpacity));
        } else {
            d3.select('.costAxis').style('opacity', newOpacity);
            d3.select('.costLine').style('opacity', newOpacity);
            this.costPoints.forEach((point) => point.style('opacity', newOpacity));
        }
    }

    private updateChart() {
        if (!this.xScale || !this.limitScale || !this.costScale) {
            return;
        }

        this.xScale.domain([0, 21]);
        this.limitScale.domain(this.scaleValues('frequency'));
        this.costScale.domain(this.scaleValues('cost'));

        const svgTransition = d3.select('#chartContainer').transition();
        const g = d3.select('.rootG');

        this.updateAxes(svgTransition);

        if (this.limitLine) {
            this.updateLimitLine(svgTransition, g);
        } else {
            this.createLimitLine(g);
        }

        if (this.costLine) {
            this.updateCostLine(svgTransition, g);
        } else {
            this.createCostLine(g);
        }

        this.updateMarkerColors();
    }

    private createScales() {
        this.xScale = d3.scaleLinear().range([0, this.width]).domain([0, 21]);

        this.limitScale = d3.scaleLinear().range([this.height, 0]).domain(this.scaleValues('frequency'));

        this.costScale = d3.scaleLinear().range([this.height, 0]).domain(this.scaleValues('cost'));
    }

    private scaleValues(paramName: string): number[] {
        let extent = [];
        if (this.data && this.data.length > 0) {
            extent = d3.extent(this.data, (d) => d[paramName]);
        } else {
            extent = [0, 0];
        }

        if (extent[1] < 0.01) {
            extent[0] = 0;
            extent[1] = 1;
        } else if (extent[1] - extent[0] < 0.01) {
            extent[0] = 0;
            extent[1] = extent[1] * 2;
        }

        const median = (extent[0] + extent[1]) / 2;
        const maxDelta = Math.max(Math.abs(extent[0] - median), Math.abs(extent[1] - median));
        if (paramName === 'frequency') {
            return [median - maxDelta * 1.35, median + maxDelta * 1.65];
        } else {
            return [median - maxDelta * 1.5, median + maxDelta * 1.5];
        }
    }

    private createAxes(g) {
        this.xAxis = d3
            .axisBottom(this.xScale)
            .ticks(20)
            .tickSizeInner(-this.height)
            .tickSizeOuter(0)
            .tickFormat((d) => {
                return d < 21 ? '' + d : '';
            });

        this.limitAxisLeft = d3.axisLeft(this.limitScale).ticks(this.limitTicksCount).tickSizeInner(-this.width).tickSizeOuter(0);

        this.costAxisRight = d3
            .axisRight(this.costScale)
            .ticks(this.costTicksCount)
            .tickSizeInner(-this.width)
            .tickSizeOuter(0)
            .tickFormat((d) => {
                return `${this.currencySymbol} ${new DecimalPipe('en').transform(d, '1.2-2')}`;
            });

        const xAxisG = g
            .append('g')
            .attr('class', 'xAxis')
            .attr('transform', this.transformTranslateParam(0, this.height))
            .call(this.xAxis);

        xAxisG.selectAll('text').attr('font-size', () => this.fontSize);

        this.limitAxisG = g.append('g').attr('class', 'limitAxis').call(this.limitAxisLeft);

        this.limitAxisG
            .selectAll('text')
            .attr('fill', () => this.colorLimitation)
            .attr('font-size', () => this.fontSize);

        this.costAxisG = g
            .append('g')
            .attr('class', 'costAxis')
            .attr('transform', this.transformTranslateParam(this.width, 0))
            .call(this.costAxisRight);

        this.costAxisG
            .selectAll('text')
            .attr('fill', () => this.colorCost)
            .attr('font-size', () => this.fontSize);

        g.selectAll('.tick line').attr('stroke', this.colorAxisLines);
    }

    private updateAxes(svgTransition) {
        svgTransition.select('.xAxis').duration(this.uiUpdateDuration).call(this.xAxis);

        svgTransition.select('.limitAxis').duration(this.uiUpdateDuration).call(this.limitAxisLeft);

        this.limitAxisG
            .selectAll('text')
            .attr('fill', () => this.colorLimitation)
            .attr('font-size', () => this.fontSize);

        svgTransition.select('.costAxis').duration(this.uiUpdateDuration).call(this.costAxisRight);

        this.costAxisG
            .selectAll('text')
            .attr('fill', () => this.colorCost)
            .attr('font-size', () => this.fontSize);

        svgTransition.selectAll('.tick line').attr('stroke', this.colorAxisLines);
    }

    private createLimitLine(g) {
        this.limitLine = d3
            .line()
            .defined((_d, index, data) => {
                return data.length > index;
            })
            .x((d) => {
                return this.xScale(d.id + 1);
            })
            .y((d) => {
                return this.limitScale(d.frequency);
            });

        g.append('path')
            .attr('class', 'limitLine')
            .style('stroke', this.colorLimitation)
            .style('stroke-width', this.chartLineWidth)
            .style('fill', 'none')
            .attr('d', this.limitLine(this.data));

        this.addLimitPoints(g, undefined);
    }

    private updateLimitLine(svgTransition, g) {
        svgTransition.select('.limitLine').duration(this.uiUpdateDuration).attr('d', this.limitLine(this.data));

        this.addLimitPoints(g, svgTransition);
    }

    private addLimitPoints(g, svgTransition) {
        this.data.forEach((value) => {
            if (this.limitPoints.length > value.id && this.limitPoints[value.id]) {
                svgTransition
                    .select('.limitPoint-' + value.id)
                    .duration(this.uiUpdateDuration)
                    .attr('transform', (d) => {
                        return this.transformTranslateParam(this.xScale(d.id + 1), this.limitScale(d.frequency));
                    });
            } else {
                const symbol = d3.symbol().size(81).type(d3.symbolCircle);

                this.limitPoints[value.id] = g
                    .selectAll('.limitPoint-' + value.id)
                    .data([value])
                    .enter()
                    .append('path')
                    .attr('class', 'limitPoint-' + value.id)
                    .attr('fill', value.id === this.selectedIteration ? this.colorSelect : this.colorLimitation)
                    .attr('stroke', value.id === this.selectedIteration ? this.colorSelect : this.colorLimitation)
                    .attr('d', symbol)
                    .attr('transform', (d) => {
                        return this.transformTranslateParam(this.xScale(d.id + 1), this.limitScale(d.frequency));
                    })
                    .on('mouseover', () => {
                        this.hoverEvent(value.id);
                    })
                    .on('mouseout', () => {
                        this.hoverEvent(undefined);
                    })
                    .on('click', () => {
                        this.iterationClick.emit(value.id);
                    });
            }
        });
    }

    private createCostLine(g) {
        this.costLine = d3
            .line()
            .defined((_d, index, data) => {
                return data.length > index;
            })
            .x((d) => {
                return this.xScale(d.id + 1);
            })
            .y((d) => {
                return this.costScale(d.cost);
            });

        g.append('path')
            .attr('class', 'costLine')
            .style('stroke', this.colorCost)
            .style('stroke-width', this.chartLineWidth)
            .style('fill', 'none')
            .attr('d', this.costLine(this.data));

        this.addCostPoints(g, undefined);
    }

    private updateCostLine(svgTransition, g) {
        svgTransition.select('.costLine').duration(this.uiUpdateDuration).attr('d', this.costLine(this.data));

        this.addCostPoints(g, svgTransition);
    }

    private addCostPoints(g, svgTransition) {
        this.data.forEach((value) => {
            if (this.costPoints.length > value.id && this.costPoints[value.id]) {
                svgTransition
                    .select('.costPoint-' + value.id)
                    .duration(this.uiUpdateDuration)
                    .attr('transform', (d) => {
                        return this.transformTranslateParam(this.xScale(d.id + 1), this.costScale(d.cost));
                    });
            } else {
                const symbol = d3.symbol().size(81).type(d3.symbolSquare);

                this.costPoints[value.id] = g
                    .selectAll('.costPoint-' + value.id)
                    .data([value])
                    .enter()
                    .append('path')
                    .attr('class', 'costPoint-' + value.id)
                    .attr('fill', value.id === this.selectedIteration ? this.colorSelect : this.colorCost)
                    .attr('stroke', value.id === this.selectedIteration ? this.colorSelect : this.colorCost)
                    .attr('d', symbol)
                    .attr('transform', (d) => {
                        return this.transformTranslateParam(this.xScale(d.id + 1), this.costScale(d.cost));
                    })
                    .on('mouseover', () => {
                        this.hoverEvent(value.id);
                    })
                    .on('mouseout', () => {
                        this.hoverEvent(undefined);
                    })
                    .on('click', () => {
                        this.iterationClick.emit(value.id);
                    });
            }
        });
    }

    private updateMarkerColors() {
        for (let i = 0; i < this.limitPoints.length; i++) {
            if (i === this.selectedIteration) {
                this.limitPoints[i].attr('fill', this.colorSelect).attr('stroke', this.colorSelect);
                this.costPoints[i].attr('fill', this.colorSelect).attr('stroke', this.colorSelect);
            } else if (i === this.hoveredIteration) {
                this.limitPoints[i].attr('fill', this.colorHoverLimitation).attr('stroke', this.colorHoverLimitation);
                this.costPoints[i].attr('fill', this.colorHoverCost).attr('stroke', this.colorHoverCost);
            } else {
                this.limitPoints[i]?.attr('fill', this.colorLimitation).attr('stroke', this.colorLimitation);
                this.costPoints[i]?.attr('fill', this.colorCost).attr('stroke', this.colorCost);
            }
        }
    }

    private transformTranslateParam(x: number, y: number): string {
        return `translate(${x}, ${y})`;
    }

    private hoverEvent(iterationId: number) {
        this.hoveredIteration = iterationId;
        this.iterationHover.emit(this.hoveredIteration);
        this.updateMarkerColors();
    }
}
